import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, MapPin, Send, Loader2 } from "lucide-react";
import { submitContactMessage } from "@/lib/contact.functions";
import { TextGenerateEffect } from "@/components/text-generate-effect";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Pluggz — Get in touch" },
      {
        name: "description",
        content:
          "Questions, partnerships, press, or creator support? Send the Pluggz team a message and we'll get back to you within 1–2 business days.",
      },
      { property: "og:title", content: "Contact Pluggz" },
      {
        property: "og:description",
        content: "Reach the Pluggz team — partnerships, creator support, and press.",
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const submit = useServerFn(submitContactMessage);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || !email || message.length < 10) {
      toast.error("Please fill in your name, email, and a message (10+ characters).");
      return;
    }

    setSubmitting(true);
    try {
      await submit({ data: { name, email, subject: form.subject.trim(), message } });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-5xl px-6 py-20">
      <header className="max-w-2xl">
        <p className="editorial-eyebrow">Contact</p>
        <h1 className="mt-3 font-serif text-5xl leading-tight tracking-tight">
          Let's talk.
        </h1>
        <TextGenerateEffect
          words="Partnerships, creator support, press, or just saying hello — we read every message. Expect a reply within 1–2 business days."
          className="mt-4 text-base text-muted-foreground"
          duration={0.4}
          staggerDelay={0.04}
        />
      </header>

      <div className="mt-14 grid gap-12 md:grid-cols-[1fr_1.4fr]">
        <aside className="space-y-8">
          <InfoRow
            icon={<Mail className="h-4 w-4" />}
            label="Email"
            value="hello@pluggz.co"
            href="mailto:hello@pluggz.co"
          />
          <InfoRow
            icon={<MessageSquare className="h-4 w-4" />}
            label="Creator support"
            value="creators@pluggz.co"
            href="mailto:creators@pluggz.co"
          />
          <InfoRow
            icon={<MapPin className="h-4 w-4" />}
            label="Studio"
            value="London, United Kingdom"
          />

          <div className="rounded-lg border border-border/60 bg-card p-5 text-sm text-muted-foreground">
            Want to join as a creator?{" "}
            <Link to="/apply" className="text-foreground underline-offset-4 hover:underline">
              Apply here
            </Link>
            .
          </div>
        </aside>

        <section className="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
          {sent ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
              <div className="rounded-full bg-primary/10 p-4 text-primary">
                <Send className="h-6 w-6" />
              </div>
              <h2 className="mt-4 font-serif text-2xl">Thanks — message received.</h2>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                We'll get back to you at the email you provided. In the meantime, feel free to keep exploring.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name" htmlFor="name">
                  <input
                    id="name"
                    name="name"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    autoComplete="name"
                  />
                </Field>
                <Field label="Email" htmlFor="email">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={255}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    autoComplete="email"
                  />
                </Field>
              </div>
              <Field label="Subject" htmlFor="subject" optional>
                <input
                  id="subject"
                  name="subject"
                  maxLength={200}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className={inputCls}
                  placeholder="What's this about?"
                />
              </Field>
              <Field label="Message" htmlFor="message">
                <textarea
                  id="message"
                  name="message"
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-y`}
                />
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  {form.message.length}/4000
                </p>
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send message
                  </>
                )}
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-foreground/60 focus:ring-2 focus:ring-foreground/10";

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 flex items-center justify-between text-xs font-medium uppercase tracking-widest text-muted-foreground">
        <span>{label}</span>
        {optional ? <span className="text-[10px] normal-case tracking-normal text-muted-foreground/70">Optional</span> : null}
      </label>
      {children}
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}) {
  const content = (
    <div className="group flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground transition-colors group-hover:text-foreground">
        {icon}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">
      {content}
    </a>
  ) : (
    content
  );
}